/**
 * EditablePreview — universal preview/edit/save shell.
 *
 * Phase 1 reference implementation for the Universal Editable Preview
 * Mode. The Ebook builder uses this; Offer / Sales Page / Lead Magnet /
 * Email Sequence / Captions builders will reuse it in Phase 2 by passing
 * different `previewSlot` and `editSlot` render props.
 *
 * IMPORTANT — never touches Business Memory. Edits are intentional
 * customisation of the generated asset only. Saving promotes the
 * asset to `final_saved_version`, but the Business Memory profile
 * fields (niche, audience, default pricing) are unchanged.
 *
 * Slots:
 *   previewSlot({ value })            — renders read-only preview
 *   editSlot   ({ value, setValue })  — renders editable form
 *
 * Props:
 *   draftKey       — localStorage key used by useDraftAutosave
 *   originalValue  — the freshly-generated content (immutable reference)
 *   onSave(value)  — async; called when user clicks "Save Final".
 *                    Should PATCH the asset row server-side and resolve.
 *   downloads      — array of { label, onClick, format, testid? }
 *                    e.g. [{label: "Download PDF", onClick: dlPdf, format: "PDF"}]
 *   busy           — show spinner / disable buttons while parent is saving
 *   savedAt        — last server-side save timestamp (for the badge)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  PencilSimple,
  ArrowsCounterClockwise,
  FloppyDisk,
  ArrowDown,
  Eye,
  Check,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import useDraftAutosave from '@/hooks/useDraftAutosave';

const formatRelative = (ts) => {
  if (!ts) return null;
  const diff = Math.max(0, Date.now() - new Date(ts).getTime());
  if (diff < 5_000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return new Date(ts).toLocaleString();
};

// Human-friendly "last saved 12 seconds ago" / "last saved 2 minutes ago"
// for the autosave-restored banner. Switches from seconds → minutes at 60s
// per spec; falls back to absolute time after 1h so the banner stays
// truthful on a long-idle tab.
const formatAutosaveAge = (epoch) => {
  if (!epoch) return null;
  const diff = Math.max(0, Date.now() - epoch);
  if (diff < 60_000) {
    const s = Math.max(1, Math.floor(diff / 1000));
    return `${s} second${s === 1 ? '' : 's'} ago`;
  }
  if (diff < 3600_000) {
    const m = Math.floor(diff / 60_000);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  return `at ${new Date(epoch).toLocaleTimeString()}`;
};

export default function EditablePreview({
  draftKey,
  originalValue,
  onSave,
  downloads = [],
  busy = false,
  savedAt = null,
  previewSlot,
  editSlot,
  className = '',
}) {
  // Working copy = what the user is currently looking at.
  const [value, setValue] = useState(originalValue);
  // Mode: 'preview' (read-only) vs 'edit' (form fields).
  const [mode, setMode] = useState('preview');
  // status: 'draft' | 'edited' | 'final' (mirrors the spec — this is the
  // CLIENT-SIDE status. The server-side row keeps `original_generated_version`
  // and `final_saved_version` only.)
  const [status, setStatus] = useState('draft');
  const [lastSavedAt, setLastSavedAt] = useState(savedAt);

  // Re-init when a fresh asset is generated.
  useEffect(() => {
    setValue(originalValue);
    setStatus('draft');
    setMode('preview');
  }, [originalValue]);

  // Browser-only autosave (10s + 500ms debounce). Never hits the network.
  const { restored, clear: clearDraft } = useDraftAutosave(draftKey, value, !!draftKey);

  // ──────────────────────────────────────────────────────────
  // P1 — "Restored from autosave" banner + Discard Draft
  //
  // When a previous session's draft is found in localStorage AND it
  // differs from the freshly-generated original, AUTO-restore it (so
  // the user doesn't lose their work to a flaky toast click) and show
  // a persistent banner with a "Discard Draft" escape hatch.
  //
  // Discard Draft: localStorage-only — never touches the DB,
  // final_saved_version, original_generated_version, or business_memory.
  // ──────────────────────────────────────────────────────────
  const [autosaveRestoredAt, setAutosaveRestoredAt] = useState(null);
  const [showAutosaveBanner, setShowAutosaveBanner] = useState(false);
  const [restoreApplied, setRestoreApplied] = useState(false);
  const [, forceTick] = useState(0); // re-render every 30s so the timestamp stays fresh

  useEffect(() => {
    if (!restored || restoreApplied) return;
    const isDifferent = JSON.stringify(restored.value) !== JSON.stringify(originalValue);
    if (!isDifferent) return;
    // Auto-apply the restored draft + surface the banner.
    setValue(restored.value);
    setStatus('edited');
    setMode('edit');
    setAutosaveRestoredAt(restored.savedAt || Date.now());
    setShowAutosaveBanner(true);
    setRestoreApplied(true);
  }, [restored, originalValue, restoreApplied]);

  // Re-render every 30s while the banner is visible so the
  // "X seconds ago" / "X minutes ago" label stays truthful.
  useEffect(() => {
    if (!showAutosaveBanner) return undefined;
    const t = window.setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => window.clearInterval(t);
  }, [showAutosaveBanner]);

  const handleDiscardDraft = useCallback(() => {
    // 1) Browser-only — wipe the autosaved draft.
    clearDraft();
    // 2) Restore the freshly-generated original value.
    setValue(originalValue);
    // 3) Reset client-side status.
    setStatus('draft');
    setMode('preview');
    // 4) Hide the banner immediately.
    setShowAutosaveBanner(false);
    setAutosaveRestoredAt(null);
    toast.success('Draft discarded — original content restored.');
  }, [clearDraft, originalValue]);

  const handleEdit = () => setMode('edit');

  const handleRePreview = () => {
    setMode('preview');
    setStatus('edited');
  };

  const handleSetValue = useCallback((next) => {
    setValue(next);
    setStatus('edited');
  }, []);

  const handleSave = async () => {
    try {
      await onSave(value);
      setStatus('final');
      setMode('preview');
      setLastSavedAt(new Date().toISOString());
      clearDraft();
      // Acceptance test 4 — banner must NOT reappear after a final save.
      setShowAutosaveBanner(false);
      setAutosaveRestoredAt(null);
      toast.success('Saved. Your final version is ready to download.');
    } catch (err) {
      console.error('[EditablePreview] save failed', err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save. Try again.');
    }
  };

  const StatusBadge = () => {
    const map = {
      draft: { label: 'Draft', cls: 'bg-slate-700/50 text-slate-300 border-slate-600' },
      edited: { label: 'Edited', cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
      final: { label: 'Final', cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
    };
    const cfg = map[status] || map.draft;
    return (
      <span
        className={`text-[11px] px-2 py-0.5 rounded border ${cfg.cls}`}
        data-testid="editable-preview-status"
      >
        {cfg.label}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`bg-white/5 border border-white/10 rounded-xl ${className}`}
      data-testid="editable-preview"
    >
      {/* Action bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center space-x-3">
          <h4 className="text-white text-sm font-semibold">
            {mode === 'edit' ? 'Edit your asset' : 'Preview'}
          </h4>
          <StatusBadge />
          {lastSavedAt && (
            <span className="text-[11px] text-slate-500" data-testid="editable-preview-saved-at">
              Saved {formatRelative(lastSavedAt)}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {mode === 'preview' ? (
            <button
              type="button"
              onClick={handleEdit}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg flex items-center space-x-1.5 transition-colors"
              data-testid="editable-preview-edit-btn"
            >
              <PencilSimple size={14} />
              <span>Edit</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleRePreview}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg flex items-center space-x-1.5 transition-colors"
              data-testid="editable-preview-repreview-btn"
            >
              <Eye size={14} />
              <span>Re-Preview</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setValue(originalValue)}
            disabled={status === 'draft' || busy}
            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-medium rounded-lg flex items-center space-x-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="editable-preview-revert-btn"
            title="Discard your edits and restore the freshly-generated version"
          >
            <ArrowsCounterClockwise size={14} />
            <span>Revert</span>
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || status === 'draft'}
            className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-xs font-semibold rounded-lg flex items-center space-x-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="editable-preview-save-btn"
          >
            {status === 'final' ? <Check size={14} /> : <FloppyDisk size={14} />}
            <span>{busy ? 'Saving…' : status === 'final' ? 'Saved' : 'Save Final'}</span>
          </button>
        </div>
      </div>

      {/* P1 — Restored-from-autosave banner. Sits ABOVE the preview/edit
          body so the user sees it the moment the page mounts with a
          recovered draft applied. Discard Draft is a localStorage-only
          escape hatch; it never touches the database. */}
      {showAutosaveBanner && (
        <div
          className="mx-4 mt-3 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between gap-3"
          data-testid="editable-preview-autosave-banner"
          role="status"
        >
          <p className="text-amber-200 text-xs">
            <span className="font-semibold">Restored from autosave</span>
            {autosaveRestoredAt && (
              <span className="text-amber-300/80">
                {' '}— last saved{' '}
                <span data-testid="editable-preview-autosave-age">
                  {formatAutosaveAge(autosaveRestoredAt)}
                </span>
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={handleDiscardDraft}
            className="px-2.5 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-[11px] font-medium rounded-md transition-colors flex-shrink-0"
            data-testid="editable-preview-discard-draft-btn"
            title="Delete the autosaved draft and reload the originally-generated version"
          >
            Discard Draft
          </button>
        </div>
      )}

      {/* Body */}
      <div className="p-4">
        {mode === 'preview'
          ? previewSlot({ value })
          : editSlot({ value, setValue: handleSetValue })}
      </div>

      {/* Downloads */}
      {downloads.length > 0 && (
        <div className="px-4 pb-4 pt-2 border-t border-white/10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {downloads.map((d, i) => (
              <button
                key={d.testid || `${d.label}-${i}`}
                type="button"
                onClick={d.onClick}
                disabled={d.disabled || busy}
                className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid={d.testid || `editable-preview-download-${(d.format || d.label).toLowerCase()}-btn`}
              >
                <ArrowDown size={14} />
                <span>{d.label}</span>
              </button>
            ))}
          </div>
          <p className="text-slate-500 text-[11px] text-center mt-2">
            Downloads use your latest saved version{status === 'edited' ? ' — save to apply your edits' : ''}.
          </p>
        </div>
      )}
    </motion.div>
  );
}
